/**
 * 自社EC (services/ec/frontend) の URL 導出 (#1184 で EcEntryCard.tsx から抽出して共用)。
 *
 * VITE_EC_URL が build-time に baked されていればそれを使い、無ければ Cloud Run
 * preview の URL pattern から派生する (preview deploy 時に EC URL を予測 baked
 * するのが難しい chicken-and-egg を回避)。
 * - main URL: https://yokoso-pay-preview-pr-{N}-{SUFFIX}.run.app
 * - ec   URL: https://yokoso-pay-ec-preview-pr-{N}-{SUFFIX}.run.app
 * SUFFIX は GCP project 単位で共通なので string 置換で EC URL を確実に構築できる。
 * demo/prod build では VITE_EC_URL を明示的に設定する想定なのでこの fallback は
 * 走らない。未解決なら空文字 (呼び出し側は導線を非描画にする)。
 */
export function deriveEcUrl(): string {
  const envUrl = import.meta.env.VITE_EC_URL
  if (envUrl) return envUrl
  if (typeof window === 'undefined') return ''
  const host = window.location.host
  if (/^yokoso-pay-preview-pr-\d+-/.test(host)) {
    const ecHost = host.replace(/^yokoso-pay-/, 'yokoso-pay-ec-')
    return `${window.location.protocol}//${ecHost}`
  }
  return ''
}

/**
 * #2237 ショップ (EC) の「YOKOSO PAY から入り直す」から来たときの戻り先。
 *
 * EC は端末の確認が要る状態で、まだ払えない端末にこのボタンを出す。PAY のホームに
 * `?ec_next=/order/confirm` のように**ショップの中の道筋だけ**を付けて来るので、
 * ここで覚えておき、ショップへ渡すときに `next` として渡す (ショップがログインし終えてから移る)。
 *
 * **道筋だけを受け取る。** `//evil.example` や `https://...` を受け取ると、
 * ログインの印 (handoff) を付けたまま外へ飛ばせてしまうので、`/` で始まる
 * ショップの中の道筋以外は捨てる。印はショップの入口 (`/`) に付けて渡すので、
 * 戻り先がどこでも印の行き先はショップの入口から変わらない。
 */
const EC_NEXT_KEY = 'ecNext'

export function safeEcPath(raw: string | null | undefined): string | null {
  if (!raw) return null
  // `/` の次に `/` や `\` が来るもの (`//host`・`/\host`) はブラウザが別のホストと読むので捨てる
  if (!/^\/(?![/\\])[A-Za-z0-9\-._~/%]*$/.test(raw)) return null
  return raw
}

/** 戻り先を覚える (読めない値は覚えない)。覚えたら true。 */
export function rememberEcNext(raw: string | null): boolean {
  const next = safeEcPath(raw)
  if (!next) return false
  try {
    sessionStorage.setItem(EC_NEXT_KEY, next)
    return true
  } catch {
    return false // 覚えられなくてもショップの入口には行ける
  }
}

/** 戻り先を覚えているか (カードを目立たせる)。 */
export function hasEcNext(): boolean {
  try {
    return !!safeEcPath(sessionStorage.getItem(EC_NEXT_KEY))
  } catch {
    return false
  }
}

/** ショップの入口の URL。戻り先を覚えていれば `next` に付ける。読んだら忘れる。 */
export function ecEntryUrl(ecUrl: string): URL {
  const u = new URL(ecUrl)
  u.pathname = '/'
  let next: string | null = null
  try {
    next = safeEcPath(sessionStorage.getItem(EC_NEXT_KEY))
    sessionStorage.removeItem(EC_NEXT_KEY)
  } catch { /* 無ければ入口へ */ }
  if (next) u.searchParams.set('next', next)
  return u
}
